import React from 'react';
import * as Types from 'types';

type Props = {
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  fields?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  groupsSize?: number;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
};

export const SegmentedInput: React.FC<Props> = ({
  fields = 6,
  autoFocus = true,
  groupsSize,
  onChange,
  onComplete,
  ...props
}) => {
  const id = React.useRef(+new Date());
  const [values, setValues] = React.useState<string[]>(Array(fields).fill(''));

  const refs = React.useRef<any[]>([]);
  if (refs.current.length === 0) {
    for (let i = 0; i < fields; i++) {
      refs.current.push(React.createRef());
    }
  }

  React.useEffect(() => {
    const val = values.join('');
    onChange && onChange(val);
    if (onComplete && val.length >= fields) {
      onComplete(val);
    }
  }, [values, onChange, onComplete]);

  const handleOnChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { currentTarget } = event;
    const { value } = currentTarget;

    if (value === '') {
      return;
    }

    const index = parseInt(currentTarget.dataset.id || '', 10);

    const next = refs.current[index + 1];
    const newValues = Object.assign([...values], { [index]: value });
    if (next) {
      next.current.focus();
      next.current.select();
    }

    setValues(newValues);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const index = parseInt(event.currentTarget.dataset.id || '');
    const prevIndex = index - 1;
    const nextIndex = index + 1;
    const prev = refs.current[prevIndex];
    const next = refs.current[nextIndex];
    switch (event.keyCode) {
      case Types.KEYS.BACKSPACE:
        event.preventDefault();
        const newValues = [...values];
        if (values[index]) {
          newValues[index] = '';
          setValues(newValues);
        } else if (prev) {
          newValues[prevIndex] = '';
          prev.current.focus();
          setValues(newValues);
        }
        break;
      case Types.KEYS.ARROW_LEFT:
        event.preventDefault();
        if (prev) {
          prev.current.focus();
        }
        break;
      case Types.KEYS.ARROW_RIGHT:
        event.preventDefault();
        if (next) {
          next.current.focus();
        }
        break;
      case Types.KEYS.ARROW_UP:
      case Types.KEYS.ARROW_DOWN:
        event.preventDefault();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`segmented-input ${props.className || ''}`}
      style={{ maxWidth: fields * 70, ...props.style }}
    >
      {values.map((value, index) => (
        <React.Fragment key={`${id.current}-${index}`}>
          <input
            type='text'
            className='segmented-input__char'
            style={props.inputStyle}
            autoFocus={index === 0}
            data-id={index}
            value={value}
            ref={refs.current[index]}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            onFocus={(event) => event.currentTarget.select()}
            disabled={props.disabled}
            maxLength={1}
            required
          />
          {groupsSize &&
          (index + 1) % groupsSize === 0 &&
          index !== values.length - 1 ? (
            <span
              className='segmented-input__break'
              key={`${id.current}-${index}-break`}
            />
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
};
